import React, { useState, useEffect, useCallback } from "react";
import { API_URL } from "../config";
import "./GeneradorLab.css";

const DEFAULT_NEGATIVE = "deformed, bad anatomy, disfigured, poorly drawn face, mutation, mutated, extra limb, ugly, poorly drawn hands, missing limb, blurry, floating limbs, disconnected limbs, malformed hands, blur, out of focus, long neck, disgusting, poorly drawn, mangled, extra fingers, duplicate artifacts, morbid, mutilated, out of frame, bad proportions, gross proportions, illustration, painting, cartoon, anime, 3d render, cgi, sketch, watermark, signature, text, logo, banner, noise, grain artifact";

const DEFAULT_LORAS = [
  { enabled: true, name: "add-detail-xl.safetensors", weight: 0.8 },
  { enabled: true, name: "Touch_of_Realism_SDXL_V2.safetensors", weight: 0.5 },
  { enabled: true, name: "aidmaBokehmachine-V0.1.safetensors", weight: 0.7 },
  { enabled: true, name: "Touch_of_Grain.safetensors", weight: 0.35 },
];

const STORAGE_KEY = "720mt_lab";
const PRESETS_KEY = "720mt_lab_presets";

function loadSaved() {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY)); } catch { return null; }
}

function loadCustomPresets() {
  try { return JSON.parse(localStorage.getItem(PRESETS_KEY)) || {}; } catch { return {}; }
}

function saveCustomPresets(presets) {
  localStorage.setItem(PRESETS_KEY, JSON.stringify(presets));
}

export default function GeneradorLab() {
  // Options from backend
  const [options, setOptions] = useState(null);

  // Config state
  const saved = loadSaved();
  const [engine, setEngine] = useState(saved?.engine || "sdxl");
  const [ckptName, setCkptName] = useState(saved?.ckptName || "Juggernaut_X_RunDiffusion_Hyper.safetensors");
  const [steps, setSteps] = useState(saved?.steps ?? 8);
  const [cfg, setCfg] = useState(saved?.cfg ?? 2.0);
  const [samplerName, setSamplerName] = useState(saved?.samplerName || "dpmpp_sde");
  const [scheduler, setScheduler] = useState(saved?.scheduler || "karras");
  const [loras, setLoras] = useState(saved?.loras || DEFAULT_LORAS);
  const [positive, setPositive] = useState(saved?.positive || "");
  const [negative, setNegative] = useState(saved?.negative || DEFAULT_NEGATIVE);
  const [width, setWidth] = useState(saved?.width ?? 1024);
  const [height, setHeight] = useState(saved?.height ?? 576);
  const [seed, setSeed] = useState(saved?.seed ?? -1);
  const [guidance, setGuidance] = useState(saved?.guidance ?? 4.0);

  // Custom presets
  const [customPresets, setCustomPresets] = useState(loadCustomPresets);

  // Slots
  const [slotA, setSlotA] = useState(null);
  const [slotB, setSlotB] = useState(null);
  const [loadingA, setLoadingA] = useState(false);
  const [loadingB, setLoadingB] = useState(false);

  // Persist config
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      engine, ckptName, steps, cfg, samplerName, scheduler, loras,
      positive, negative, width, height, seed, guidance,
    }));
  }, [engine, ckptName, steps, cfg, samplerName, scheduler, loras, positive, negative, width, height, seed, guidance]);

  // Load options and validate current config against available models
  useEffect(() => {
    fetch(`${API_URL}/lab/options`)
      .then(r => r.json())
      .then(opts => {
        setOptions(opts);
        // If checkpoints available and current not in list, switch to first
        if (opts.checkpoints?.length > 0) {
          if (!opts.checkpoints.includes(ckptName)) {
            setCkptName(opts.checkpoints[0]);
          }
        }
      })
      .catch(() => {});
  }, []);

  const loadPreset = (key) => {
    if (key === "blank") {
      const firstCkpt = options?.checkpoints?.[0] || "sd_xl_base_1.0.safetensors";
      setEngine("sdxl");
      setCkptName(firstCkpt);
      setSteps(8);
      setCfg(2.0);
      setSamplerName("dpmpp_sde");
      setScheduler("karras");
      setLoras(prev => prev.map(l => ({ ...l, enabled: false })));
      setPositive("");
      setNegative(DEFAULT_NEGATIVE);
      return;
    }
    if (!options?.presets?.[key]) return;
    const p = options.presets[key];
    setEngine(p.engine || "sdxl");
    if (p.ckpt_name) {
      // Use preset checkpoint if available, otherwise first available
      const avail = options?.checkpoints || [];
      setCkptName(avail.includes(p.ckpt_name) ? p.ckpt_name : (avail[0] || p.ckpt_name));
    }
    if (p.steps) setSteps(p.steps);
    if (p.cfg !== undefined) setCfg(p.cfg);
    if (p.sampler_name) setSamplerName(p.sampler_name);
    if (p.scheduler) setScheduler(p.scheduler);
    if (p.guidance !== undefined) setGuidance(p.guidance);
    // Disable LoRAs for flux2 or if they don't exist in available list
    if (p.engine === "flux2") {
      setLoras(prev => prev.map(l => ({ ...l, enabled: false })));
    }
  };

  const savePreset = () => {
    const name = prompt("Nombre del preset:");
    if (!name || !name.trim()) return;
    const key = name.trim().toLowerCase().replace(/\s+/g, "_");
    const preset = {
      label: name.trim(),
      engine, ckpt_name: ckptName, steps, cfg, sampler_name: samplerName,
      scheduler, guidance, loras: JSON.parse(JSON.stringify(loras)),
      positive, negative,
    };
    const updated = { ...customPresets, [key]: preset };
    setCustomPresets(updated);
    saveCustomPresets(updated);
  };

  const deletePreset = (key) => {
    const updated = { ...customPresets };
    delete updated[key];
    setCustomPresets(updated);
    saveCustomPresets(updated);
  };

  const loadCustomPreset = (key) => {
    const p = customPresets[key];
    if (!p) return;
    setEngine(p.engine || "sdxl");
    if (p.ckpt_name) setCkptName(p.ckpt_name);
    if (p.steps) setSteps(p.steps);
    if (p.cfg !== undefined) setCfg(p.cfg);
    if (p.sampler_name) setSamplerName(p.sampler_name);
    if (p.scheduler) setScheduler(p.scheduler);
    if (p.guidance !== undefined) setGuidance(p.guidance);
    if (p.loras) setLoras(p.loras);
    if (p.positive !== undefined) setPositive(p.positive);
    if (p.negative !== undefined) setNegative(p.negative);
  };

  const buildRequest = () => ({
    positive, negative: engine === "flux2" ? "" : negative,
    width, height, seed, engine,
    ckpt_name: ckptName, steps, cfg, sampler_name: samplerName,
    scheduler, loras: engine === "flux2" ? [] : loras,
    unet_name: "flux2_dev_fp8mixed.safetensors",
    clip_name: "mistral_3_small_flux2_bf16.safetensors",
    vae_name: "flux2-vae.safetensors",
    guidance,
  });

  const configSummary = (req, result) => {
    const parts = [];
    if (req.engine === "flux2") {
      parts.push("Flux2");
    } else {
      const short = req.ckpt_name.replace(".safetensors", "").slice(0, 20);
      parts.push(short);
    }
    parts.push(`${req.steps}st`);
    parts.push(`CFG:${req.cfg}`);
    parts.push(req.sampler_name);
    if (result?.seed !== undefined) parts.push(`seed:${result.seed}`);
    return parts.join(" · ");
  };

  const generate = useCallback(async (slot) => {
    const setLoading = slot === "A" ? setLoadingA : setLoadingB;
    const setSlot = slot === "A" ? setSlotA : setSlotB;
    const req = buildRequest();

    setLoading(true);
    setSlot(null);
    try {
      const token = localStorage.getItem("token") || "";
      const res = await fetch(`${API_URL}/lab/generate`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(req),
      });
      if (!res.ok) {
        let errMsg = `Error ${res.status}`;
        try { const err = await res.json(); errMsg = err.detail || errMsg; } catch { /* non-JSON error */ }
        setSlot({ error: errMsg });
        return;
      }
      const data = await res.json();
      setSlot({
        image: data.image,
        seed: data.seed,
        width: data.width,
        height: data.height,
        config: configSummary(req, data),
        request: req,
      });
    } catch (e) {
      setSlot({ error: e.message });
    } finally {
      setLoading(false);
    }
  }, [positive, negative, width, height, seed, engine, ckptName, steps, cfg, samplerName, scheduler, loras, guidance]);

  const exportConfig = () => {
    const req = buildRequest();
    delete req.positive;
    delete req.negative;
    navigator.clipboard.writeText(JSON.stringify(req, null, 2));
    alert("Configuración copiada al portapapeles");
  };

  const updateLora = (idx, field, value) => {
    setLoras(prev => prev.map((l, i) => i === idx ? { ...l, [field]: value } : l));
  };

  const checkpoints = options?.checkpoints || [ckptName];
  const loraOptions = options?.loras || [];
  const samplers = options?.samplers || [samplerName];
  const schedulers = options?.schedulers || [scheduler];
  const presetKeys = options?.presets ? Object.keys(options.presets) : [];

  return (
    <div className="lab-page">
      {/* Comparación */}
      <div className="lab-compare">
        {["A", "B"].map(slot => {
          const data = slot === "A" ? slotA : slotB;
          const loading = slot === "A" ? loadingA : loadingB;
          return (
            <div className="lab-slot" key={slot}>
              <div className="lab-slot-header">
                <span>Slot {slot}</span>
                {data?.config && <span>{data.config}</span>}
              </div>
              <div className="lab-slot-image">
                {loading && (
                  <div className="lab-slot-loading">
                    <div className="generar-loading-dot" />
                    <span style={{fontSize: "0.75rem"}}>Generando...</span>
                  </div>
                )}
                {!loading && data?.image && (
                  <img src={`data:image/png;base64,${data.image}`} alt={`Slot ${slot}`} />
                )}
                {!loading && data?.error && (
                  <div style={{color: "#ef4444", fontSize: "0.75rem", padding: 12, textAlign: "center"}}>{data.error}</div>
                )}
                {!loading && !data && (
                  <div className="lab-slot-empty">
                    <span style={{fontSize: "1.5rem"}}>{ slot === "A" ? "A" : "B" }</span>
                    <span>Pulsa Gen → {slot}</span>
                  </div>
                )}
              </div>
              {data?.seed !== undefined && (
                <div className="lab-slot-meta">
                  {data.width}x{data.height} · seed {data.seed}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Controles */}
      <div className="lab-controls">
        {/* Preset */}
        <div className="lab-section">
          <div className="lab-section-title">Preset</div>
          <div className="lab-preset-row">
            <select className="lab-select" defaultValue="" onChange={e => {
              const v = e.target.value;
              if (!v) return;
              if (v === "blank") loadPreset("blank");
              else if (v.startsWith("custom:")) loadCustomPreset(v.replace("custom:", ""));
              else loadPreset(v);
              e.target.value = "";
            }}>
              <option value="">Cargar preset...</option>
              <option value="blank">En blanco (sin LoRAs)</option>
              {presetKeys.map(k => <option key={k} value={k}>{k}</option>)}
              {Object.keys(customPresets).length > 0 && <option disabled>── Mis presets ──</option>}
              {Object.entries(customPresets).map(([k, v]) => (
                <option key={`custom:${k}`} value={`custom:${k}`}>{v.label || k}</option>
              ))}
            </select>
            <button className="lab-btn lab-btn--preset" onClick={savePreset} style={{flex: "none", padding: "4px 10px"}}>
              Guardar
            </button>
          </div>
          {Object.keys(customPresets).length > 0 && (
            <div style={{display: "flex", flexWrap: "wrap", gap: 4, marginTop: 4}}>
              {Object.entries(customPresets).map(([k, v]) => (
                <span key={k} style={{fontSize: "0.65rem", background: "rgba(118,25,96,0.08)", padding: "2px 6px", borderRadius: 4, display: "flex", alignItems: "center", gap: 4}}>
                  {v.label || k}
                  <span style={{cursor: "pointer", color: "#ef4444", fontWeight: 700}} onClick={() => deletePreset(k)}>x</span>
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Motor */}
        <div className="lab-section">
          <div className="lab-section-title">Motor</div>
          <div className="lab-row">
            <span className="lab-label">Engine</span>
            <select className="lab-select" value={engine} onChange={e => setEngine(e.target.value)}>
              <option value="sdxl">SDXL</option>
              <option value="flux2">Flux 2</option>
            </select>
          </div>

          {engine === "sdxl" && (
            <div className="lab-row">
              <span className="lab-label">Checkpoint</span>
              <select className="lab-select" value={ckptName} onChange={e => setCkptName(e.target.value)}>
                {checkpoints.map(c => <option key={c} value={c}>{c.replace(".safetensors", "")}</option>)}
              </select>
            </div>
          )}
        </div>

        {/* Sampler */}
        <div className="lab-section">
          <div className="lab-section-title">Sampler</div>
          <div className="lab-row">
            <span className="lab-label">Steps</span>
            <input type="range" className="lab-slider" min="1" max="50" value={steps} onChange={e => setSteps(Number(e.target.value))} />
            <span className="lab-value">{steps}</span>
          </div>
          {engine === "sdxl" && (
            <div className="lab-row">
              <span className="lab-label">CFG</span>
              <input type="range" className="lab-slider" min="0.5" max="20" step="0.5" value={cfg} onChange={e => setCfg(Number(e.target.value))} />
              <span className="lab-value">{cfg}</span>
            </div>
          )}
          {engine === "flux2" && (
            <div className="lab-row">
              <span className="lab-label">Guidance</span>
              <input type="range" className="lab-slider" min="0.5" max="20" step="0.5" value={guidance} onChange={e => setGuidance(Number(e.target.value))} />
              <span className="lab-value">{guidance}</span>
            </div>
          )}
          <div className="lab-row">
            <span className="lab-label">Sampler</span>
            <select className="lab-select" value={samplerName} onChange={e => setSamplerName(e.target.value)}>
              {samplers.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div className="lab-row">
            <span className="lab-label">Scheduler</span>
            <select className="lab-select" value={scheduler} onChange={e => setScheduler(e.target.value)}>
              {schedulers.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
        </div>

        {/* LoRAs — solo SDXL */}
        {engine === "sdxl" && (
          <div className="lab-section">
            <div className="lab-section-title">LoRAs</div>
            {loras.map((lora, i) => (
              <div key={i} className={`lab-lora-row ${!lora.enabled ? "disabled" : ""}`}>
                <input
                  type="checkbox"
                  className="lab-lora-check"
                  checked={lora.enabled}
                  onChange={e => updateLora(i, "enabled", e.target.checked)}
                />
                <select
                  className="lab-lora-select"
                  value={lora.name}
                  onChange={e => updateLora(i, "name", e.target.value)}
                  disabled={!lora.enabled}
                >
                  {loraOptions.map(l => <option key={l} value={l}>{l.replace(".safetensors", "")}</option>)}
                  {!loraOptions.includes(lora.name) && <option value={lora.name}>{lora.name.replace(".safetensors", "")}</option>}
                </select>
                <input
                  type="range"
                  className="lab-lora-slider"
                  min="0" max="1" step="0.05"
                  value={lora.weight}
                  onChange={e => updateLora(i, "weight", Number(e.target.value))}
                  disabled={!lora.enabled}
                />
                <span className="lab-lora-value">{lora.weight.toFixed(2)}</span>
              </div>
            ))}
          </div>
        )}

        {/* Prompt */}
        <div className="lab-section">
          <div className="lab-section-title">Prompt</div>
          <div className="lab-textarea-label">Positivo</div>
          <textarea
            className="lab-textarea"
            value={positive}
            onChange={e => setPositive(e.target.value)}
            placeholder="Describe la imagen..."
            rows={3}
          />
          {engine === "sdxl" && (
            <>
              <div className="lab-textarea-label" style={{marginTop: 4}}>Negativo</div>
              <textarea
                className="lab-textarea"
                value={negative}
                onChange={e => setNegative(e.target.value)}
                placeholder="Lo que NO quieres..."
                rows={2}
              />
            </>
          )}
        </div>

        {/* Resolución y Seed */}
        <div className="lab-section">
          <div className="lab-section-title">Imagen</div>
          <div className="lab-row">
            <span className="lab-label">Ancho</span>
            <input type="number" className="lab-input" value={width} onChange={e => setWidth(Number(e.target.value))} min={256} max={2048} step={64} />
          </div>
          <div className="lab-row">
            <span className="lab-label">Alto</span>
            <input type="number" className="lab-input" value={height} onChange={e => setHeight(Number(e.target.value))} min={256} max={2048} step={64} />
          </div>
          <div className="lab-row">
            <span className="lab-label">Seed</span>
            <input type="number" className="lab-input" value={seed} onChange={e => setSeed(Number(e.target.value))} />
            <button className="lab-btn lab-btn--preset" onClick={() => setSeed(-1)} style={{flex: "none", padding: "4px 8px"}}>Aleatorio</button>
          </div>
        </div>

        {/* Acciones */}
        <div className="lab-actions">
          <button className="lab-btn lab-btn--a" onClick={() => generate("A")} disabled={!positive.trim() || loadingA}>
            {loadingA ? "Generando..." : "Gen → A"}
          </button>
          <button className="lab-btn lab-btn--b" onClick={() => generate("B")} disabled={!positive.trim() || loadingB}>
            {loadingB ? "Generando..." : "Gen → B"}
          </button>
        </div>
        <div className="lab-actions">
          <button className="lab-btn lab-btn--export" onClick={exportConfig}>
            Exportar JSON
          </button>
        </div>
      </div>
    </div>
  );
}
